/*
 * Copyright (c) 2018 Barclays PLC.
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Barclays PLC.
 * ("Confidential Information").
 *
 * File: comboAppForm.js
 * Created: Nov 05, 2018
 */

import React from 'react';
import { reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { getComboAppState, getApplicationDecision, getCampaignFromEnvConfig } from '../../../selectors';
import { submitFullApp, displayFormErrorOnSubmit, updateFormValue } from '../../../actions/loanApplicationActions';
import LoanPurposeFieldSet from '../../LoanApplication/LoanPurposeFieldSet/loanPurposeFieldSet';
import NameFieldSet from '../../LoanApplication/NameFieldSet/nameFieldSet';
import MailingAddressFieldSet from '../../LoanApplication/AddressFieldSet/mailingAddressFieldSet';
import StreetAddressFieldSet from '../../LoanApplication/AddressFieldSet/streetAddressFieldSet';
import HousingFieldSet from '../../LoanApplication/HousingFieldSet/housingFieldSet';
import EmploymentFieldSet from '../../LoanApplication/EmploymentFieldSet/employmentFieldSet';
import PersonalFieldSet from '../../LoanApplication/PersonalFieldSet/personalFieldSet';
import ContactFieldSet from '../../LoanApplication/ContactFieldSet/contactFieldSet';
import LegalFieldSet from "../../LoanApplication/LegalFieldSet/legalFieldSet";
import Section from '../../Section/section';
import FeatureToggle from '../../FeatureToggle/feature.toggle';
import PropTypes from 'prop-types';
import '../../LoanApplication/applicationForm.scss';
import { pick, compose } from 'ramda';

export const ComboAppForm = (props) => {
  const { initialValues, currentValues, handleSubmit, submitting, hasAppDecision, submitFullApp, displayFormErrorOnSubmit, updateFormValue } = props;

  if (hasAppDecision) {
    return <Redirect push to="/apply/decision"/>;
  }

  const updateComboFormValue = (key, value) => {
    updateFormValue('comboApp', key, value);
  };

  return (
    <Section>
      <header>
        <p className="font-weight-normal">Almost there! Just complete and submit.</p>
      </header>
      <form autoComplete="off" noValidate="novalidate" className="application-form mt-4" onSubmit={compose(displayFormErrorOnSubmit, handleSubmit(submitFullApp))}>
        <LoanPurposeFieldSet value={currentValues.loanPurpose || initialValues.loanPurpose} loanAmount={initialValues.loanAmount} />
        <NameFieldSet isReadOnly={false} />
        <MailingAddressFieldSet />
        <StreetAddressFieldSet mailingAddress={currentValues.homeAddress || initialValues.homeAddress}/>
        <ContactFieldSet />
        <HousingFieldSet/>
        <FeatureToggle featureName="cl_always_display_income_disclosure_for_affiliates" >
          <EmploymentFieldSet
            sourceOfIncome={ currentValues.sourceOfIncome }
            occupation={ currentValues.occupation }
            updateFormValue={updateComboFormValue}
            initialValues={ initialValues }
          />
        </FeatureToggle>
        <FeatureToggle featureName="cl_always_display_income_disclosure_for_affiliates" showWhenFeatureToggleOff={true}>
          <EmploymentFieldSet
            occupation={ currentValues.occupation }
            updateFormValue={updateComboFormValue}
            sourceOfIncome={ currentValues.sourceOfIncome }
          />
        </FeatureToggle>
        <PersonalFieldSet isUsCitizen={currentValues.usCitizen} />
        <LegalFieldSet />
        <p>
          <button type="submit" disabled={submitting}>Continue</button>
        </p>
        <footer>
          <p>
            <i className="material-icons">thumb_up</i> Upon submitting the application, your credit information will be reviewed - which may affect your credit score
          </p>
        </footer>
      </form>
    </Section>
  );
};

ComboAppForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submitSucceeded: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  hasAppDecision: PropTypes.bool
};

const selector = formValueSelector('comboApp');

const mapStateToProps = state => {
  return {
    initialValues: {
      ...getComboAppState(state),
      ...pick(['campaignId', 'cellId'], getCampaignFromEnvConfig(state))
    },
    currentValues: {
      loanPurpose: selector(state, 'loanPurpose'),
      homeAddress: selector(state, 'homeAddress'),
      usCitizen: selector(state, 'usCitizen'),
      occupation: selector(state, 'occupation'),
      sourceOfIncome: selector(state, 'sourceOfIncome')

    },
    hasAppDecision: !!getApplicationDecision(state)
  };
};



const mapDispatchToProps = { submitFullApp, displayFormErrorOnSubmit, updateFormValue };

export const ComboAppReduxForm = reduxForm({
  form: 'comboApp'
})(ComboAppForm);

export default connect(mapStateToProps, mapDispatchToProps)(ComboAppReduxForm);
